import React, { useState, useCallback } from 'react'
import { Button, DatePicker, Row, Form, Input, Select } from 'antd'
import { useAppDispatch, useAppSelector } from '../../app/hooks'
import { setFilter } from '../../actions/photoWatermark'
import sensors, { sensorsRole } from '../../utils/sensors'
import DatesPicker from '../components/DatesPickerV2'
import DepartmentMemberPicker from '../components/DepartmentMemberPickerV2'
import WatermarksPicker from '../components/WatermarksPicker'
import TagPicker from '../components/TagPicker'
import CONFIG from '../../constants/config'
import { calcPickedMemberNum } from './lib/lib'

const { RangePicker } = DatePicker
const FormItem = Form.Item
const Option = Select.Option

const FilterExistPhotoCommentEmnu = {
  '0': 'all',
  '1': 'noComment',
  '2': 'haveComment'
}

function Filter({ onSubmit, onReset }) {
  const dispatch = useAppDispatch()

  // local state
  const [showDatePickerPane, setShowDatePickerPane] = useState(false)
  const [showMemberPickerModal, setShowMemberPickerModal] = useState(false)
  const [showWatermarkModal, setShowWatermarkModal] = useState(false)
  const [showTagPickerModal, setShowTagPickerModal] = useState(false)
  const [watermarks] = useState([])

  // Redux state
  const isVip = useAppSelector((state) => state.group.isVip)
  const filter = useAppSelector((state) => state.photoWatermark.filter)
  const groupId = useAppSelector((state) => state.group.groupId)
  const myRole = useAppSelector((state) => state.group.myRole)
  const members = useAppSelector((state) => state.group.members)
  const pickerUserCount = useAppSelector(
    (state) => state.organization.pickerOrgUserCount
  )

  const handleSensorClickItem = useCallback(
    (clickItem) => {
      sensors.track('web_click_page_watermark_data', {
        clickItem,
        groupID: groupId,
        role: sensorsRole(myRole),
        track_sign: 'aother.b4366.action.web_click_page_watermark_data',
        requirement_id: 521
      })
    },
    [groupId, myRole]
  )

  const handleDatesChange = useCallback(
    (startDate, endDate) => {
      setShowDatePickerPane(false)
      dispatch(
        setFilter({
          ...filter,
          times: [startDate, endDate]
        })
      )
    },
    [dispatch, filter]
  )

  const handleMemberChangeV2 = useCallback(
    (params) => {
      const { selectedDepartmentUsers, selectedDepartments } = params
      setShowMemberPickerModal(false)
      dispatch(
        setFilter({
          ...filter,
          departments: selectedDepartments,
          departmentUsers: selectedDepartmentUsers
        })
      )
    },
    [dispatch, filter]
  )

  const handleFilterModals = useCallback(
    (type) => {
      handleSensorClickItem(
        {
          time: 'choosePhotoDate',
          member: 'choosePhotoMember',
          watermark: 'chooseWatermark'
        }[type]
      )

      if (type === 'time') {
        sensors.track('web_workgroup_filter_time_click', {
          pagePath: window.location.pathname
        })
        setShowDatePickerPane((prev) => !prev)
        setShowMemberPickerModal(false)
        setShowWatermarkModal(false)
      }
      if (type === 'member') {
        setShowMemberPickerModal((prev) => !prev)
        setShowDatePickerPane(false)
        setShowWatermarkModal(false)
      }
      if (type === 'watermark') {
        setShowWatermarkModal((prev) => !prev)
        setShowDatePickerPane(false)
        setShowMemberPickerModal(false)
        setShowTagPickerModal(false)
      }
      if (type === 'tag') {
        setShowTagPickerModal((prev) => !prev)
        setShowDatePickerPane(false)
        setShowMemberPickerModal(false)
        setShowWatermarkModal(false)
      }
    },
    [handleSensorClickItem]
  )

  const handleWatermarksChange = useCallback(
    (checkedList) => {
      const ids = checkedList.map((id) => id.split('+'))
      sensors.track('web_workgroup_filter_watermark_page_click_done', {
        watermarkNum: checkedList.length,
        groupWatermarkNum: ids.filter((id) => id[1]).length,
        basicWatermarkNum: ids.filter((id) => id[0] !== '0' && id[1] === '')
          .length,
        noWatermarkNum: ids.filter((id) => id[0] === '0').length,
        pageType: 'watermarkData'
      })
      setShowWatermarkModal(false)
      dispatch(
        setFilter({
          ...filter,
          watermarks: checkedList
        })
      )
    },
    [dispatch, filter]
  )

  const handleTagChange = useCallback(
    (checkedList, matchAll) => {
      setShowTagPickerModal(false)
      dispatch(
        setFilter({
          ...filter,
          labelIDs: checkedList,
          labelMatchAll: matchAll
        })
      )
    },
    [dispatch, filter]
  )

  const handleCalcPickedMemberNum = useCallback(() => {
    return calcPickedMemberNum(filter, pickerUserCount)
  }, [filter, pickerUserCount])

  const handleCalcMemberPickedValue = useCallback(() => {
    const pickedMembersNum = handleCalcPickedMemberNum()
    if (pickedMembersNum === pickerUserCount) {
      // return `全部(${pickerUserCount}人)`
      return '全部成员'
    }
    return `已选${pickedMembersNum}人`
  }, [handleCalcPickedMemberNum, pickerUserCount])

  const handleSubmit = useCallback(() => {
    onSubmit()

    sensors.track('web_workgroup_watermarkdatapage_click_filter', {
      searchContent: [filter.keyword],
      fileType:
        filter.mediaType === null
          ? 'all'
          : filter.mediaType === 0
            ? 'photo'
            : 'video',
      dateOffset: filter.times[1].diff(filter.times[0], 'days') + 1,
      startTime: filter.times[0].format(CONFIG.timeFormat),
      endTime: filter.times[1].format(CONFIG.timeFormat),
      memberNum: handleCalcPickedMemberNum(),
      isAllMember: handleCalcPickedMemberNum() === members.length,
      watermarkNum: filter.watermarks.length,
      isAllWatermark:
        filter.watermarks.length === 0 ||
        filter.watermarks.length === watermarks.length,
      isGroupVIP: isVip,
      contentType: FilterExistPhotoCommentEmnu[filter.existPhotoComment]
    })

    handleSensorClickItem('beginSearch')
  }, [
    onSubmit,
    filter,
    members.length,
    watermarks.length,
    isVip,
    handleCalcPickedMemberNum,
    handleSensorClickItem
  ])

  // const isDatesOutOfMaxRange =
  //   Math.abs(
  //     filter.times[0].diff(filter.times[1], 'days', true)
  //   ) > 31
  const isStartTimeAfterEndTime = filter.times[0].isAfter(filter.times[1])

  return (
    <div className="Filter filter-container">
      <Row>
        <Form layout="inline">
          <FormItem label="筛选内容">
            <Input
              style={{ width: '236px' }}
              placeholder="水印关键词搜索"
              maxLength={16}
              value={filter.keyword}
              onFocus={() => handleSensorClickItem('searchContent')}
              onChange={(e) =>
                dispatch(setFilter({ ...filter, keyword: e.target.value }))
              }
            />
          </FormItem>
          <FormItem label="内容类型">
            <Select
              style={{ width: '236px' }}
              value={filter.mediaType}
              onFocus={() => {
                handleSensorClickItem('chooseFileType')
              }}
              onChange={(value) =>
                dispatch(setFilter({ ...filter, mediaType: value }))
              }
            >
              <Option value={-1}>照片与视频</Option>
              <Option value={0}>仅照片</Option>
              <Option value={1}>仅视频</Option>
            </Select>
          </FormItem>
          <FormItem label="筛选评论">
            <Select
              allowClear
              style={{ width: '236px' }}
              value={filter.existPhotoComment}
              onFocus={() => {
                handleSensorClickItem('chooseComment')
              }}
              onChange={(value) =>
                dispatch(setFilter({ ...filter, existPhotoComment: value }))
              }
            >
              <Option value={'0'}>全部</Option>
              <Option value={'1'}>无评论</Option>
              <Option value={'2'}>有评论</Option>
            </Select>
          </FormItem>
        </Form>
      </Row>
      <Row>
        <Form layout="inline">
          <FormItem
            label="筛选时间"
            validateStatus={isStartTimeAfterEndTime ? 'error' : ''}
            help={isStartTimeAfterEndTime ? '结束时间应晚于开始时间' : ''}
          >
            <RangePicker
              value={filter.times}
              open={false}
              allowClear={false}
              onOpenChange={() => handleFilterModals('time')}
              style={{ width: '236px' }}
            />
          </FormItem>
          <FormItem label="筛选成员">
            <Select
              style={{ width: '236px' }}
              value={handleCalcMemberPickedValue()}
              open={false}
              onDropdownVisibleChange={() => {
                handleFilterModals('member')
              }}
            ></Select>
          </FormItem>
          <FormItem label="筛选水印">
            <Select
              style={{ width: '236px' }}
              value={
                filter.watermarks.length === watermarks.length ||
                filter.watermarks.length === 0
                  ? '全部水印类型'
                  : `${filter.watermarks.length}个水印`
              }
              open={false}
              onDropdownVisibleChange={() => handleFilterModals('watermark')}
            ></Select>
          </FormItem>
        </Form>
      </Row>
      <Row>
        <Form layout="inline">
          <FormItem label="筛选标签">
            <Select
              style={{ width: '236px' }}
              value={
                filter.labelIDs && filter.labelIDs.length > 0
                  ? `已选${filter.labelIDs.length}个标签`
                  : '全部照片标签'
              }
              open={false}
              onDropdownVisibleChange={() => handleFilterModals('tag')}
            ></Select>
          </FormItem>
          <FormItem label="" colon={false} style={{ marginLeft: '465px' }}>
            <Button
              disabled={isStartTimeAfterEndTime}
              type="primary"
              style={{ marginRight: '10px' }}
              onClick={handleSubmit}
            >
              开始筛选
            </Button>
            <Button onClick={onReset}>重置</Button>
          </FormItem>
        </Form>
      </Row>
      <DatesPicker
        visible={showDatePickerPane}
        dates={filter.times}
        maxMonths={1}
        onChange={handleDatesChange}
        onCancel={() => setShowDatePickerPane(false)}
      />
      <DepartmentMemberPicker
        open={showMemberPickerModal}
        filterObj={filter}
        onOk={handleMemberChangeV2}
        onCancel={() => setShowMemberPickerModal(false)}
      />
      <WatermarksPicker
        visible={showWatermarkModal}
        onOK={handleWatermarksChange}
        onCancel={() => setShowWatermarkModal(false)}
        checkedList={filter.watermarks}
      />
      <TagPicker
        visible={showTagPickerModal}
        checkedList={filter.labelIDs || []}
        matchAll={filter.labelMatchAll}
        onCancel={() => setShowTagPickerModal(false)}
        onOK={handleTagChange}
      />
    </div>
  )
}

export default Filter
